import type { RefObject } from 'react'
import { useEffect, useState } from 'react'

import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import posthog from 'posthog-js'

import SearchTextNodes from '@data/i18n/search.json'

import { searchUrl } from '@lib/constants'
import type { SearchResult } from '@lib/types'

// Only type is imported — for development
import type SearchResultsComponent from './SearchResults'

const SearchResults = dynamic(
	() => import('./SearchResults') as any
) as typeof SearchResultsComponent

type SearchPageParams = {
	q?: string
	inputRef?: RefObject<HTMLInputElement>
	type?: string
}

export default function SearchPage({ q, inputRef, type }: SearchPageParams) {
	const [results, setResults] = useState<SearchResult[]>([])
	const [qry, setQry] = useState('')
	const router = useRouter()

	// set query (search) string to the router query string
	useEffect(() => {
		setQry(router.pathname === '/search' && !!q ? q : '')
	}, [q, router, inputRef])

	useEffect(() => {
		inputRef?.current?.focus()
	}, [inputRef])

	// Handle form submission
	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault()

		posthog.capture('search_submitted', { query: qry })

		if (router.pathname === '/search' && qry !== router.query.q) {
			router.push(`${searchUrl}?q=${qry}`)
		} else {
			router.push(`${searchUrl}?q=${qry}`, undefined, { shallow: true })
		}
	}

	return (
		<>
			<header className={`${type === 'compact' ? '' : 'section py-12'}`}>
				<form
					onSubmit={handleSubmit}
					className={`${
						type === 'compact'
							? ''
							: 'container flex max-lg:flex-col items-center justify-between max-w-screen-lg bg-gray-100 py-12'
					}`}
				>
					{type === 'compact' ? (
						<h1 className="text-[24px] font-[500] text-sans my-4 text-center">{SearchTextNodes.title}</h1>
					) : (
						<div className="max-lg:text-center max-lg:mb-8">
							<h1 className="text-[24px] font-[500] text-sans text-gray-500 mb-2">{SearchTextNodes.title}</h1>
							<p className="text-3xl xl:text-4xl text-center title">
								{SearchTextNodes.description}
							</p>
						</div>
					)}
					{/* TODO: Autofocus this element (and select value) whenever the search dropdown is opened */}
					<input
						ref={inputRef}
						className={`${
							type === 'compact'
								? 'block w-full max-w-md px-0 py-4 mx-auto mb-8 border-0 border-b-2 border-transparent form-input border-b-bbx-spark focus:outline-none'
								: 'input w-full max-w-md'
						}`}
						type="search"
						autoComplete="off"
						autoCorrect="off"
						autoCapitalize="off"
						spellCheck="false"
						placeholder={SearchTextNodes.placeholder}
						value={qry}
						onChange={(e) => setQry(e.target.value)}
					/>
				</form>
			</header>

			{qry !== '' ? (
				<SearchResults searchText={qry} results={results} setResults={setResults} />
			) : null}
		</>
	)
}
